'use client';

import * as React from 'react';
import { useTheme } from '../../../../stores/theme-store';
import { Monitor, Moon, Sun } from 'lucide-react';
import {
  ThemeToggler as ThemeTogglerPrimitive,
  type ThemeTogglerProps as ThemeTogglerPrimitiveProps,
  type ThemeSelection,
  type Resolved,
  type Direction,
} from '../../primitives/effects/theme-toggler';
import { cn } from '../../../../lib/utils';

const getIcon = (
  effective: ThemeSelection,
  resolved: Resolved,
  modes: ThemeSelection[],
) => {
  const theme = modes.includes('system') ? effective : resolved;
  return theme === 'system' ? (
    <Monitor className="h-4 w-4" />
  ) : theme === 'dark' ? (
    <Moon className="h-4 w-4" />
  ) : (
    <Sun className="h-4 w-4" />
  );
};

const getNextTheme = (
  effective: ThemeSelection,
  modes: ThemeSelection[],
): ThemeSelection => {
  const i = modes.indexOf(effective);
  if (i === -1) return modes[0];
  return modes[(i + 1) % modes.length];
};

export interface ThemeTogglerButtonProps extends Omit<React.ComponentProps<'button'>, 'children'> {
  modes?: ThemeSelection[];
  onImmediateChange?: ThemeTogglerPrimitiveProps['onImmediateChange'];
  direction?: Direction;
}

export function ThemeTogglerButton({
  modes = ['light', 'dark'],
  direction = 'ltr',
  onImmediateChange,
  onClick,
  className,
  ...props
}: ThemeTogglerButtonProps) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div className="h-9 w-9" />;
  }

  return (
    <ThemeTogglerPrimitive
      theme={(theme || 'light') as ThemeSelection}
      resolvedTheme={(resolvedTheme || 'light') as Resolved}
      setTheme={setTheme}
      direction={direction}
      onImmediateChange={onImmediateChange}
    >
      {({ effective, resolved, toggleTheme }) => (
        <button
          type="button"
          data-slot="theme-toggler-button"
          aria-label="تغییر تم"
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            'text-default-600 dark:text-default-400 hover:text-default-900 dark:hover:text-default-100',
            'hover:bg-default-100 dark:hover:bg-default-800/60',
            'transition-colors duration-150 cursor-pointer outline-none',
            'focus-visible:ring-2 focus-visible:ring-primary',
            className
          )}
          onClick={(e) => {
            onClick?.(e);
            toggleTheme(getNextTheme(effective, modes), e);
          }}
          {...props}
        >
          {getIcon(effective, resolved, modes)}
        </button>
      )}
    </ThemeTogglerPrimitive>
  );
}

export default ThemeTogglerButton;
